import { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, Check, Eraser, ArrowLeft, FileText } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, SERVICE_LABELS } from "@/lib/api";
import { trackPageview } from "@/lib/analytics";
import { CONTACT } from "@/config/content";

export default function VertragPage() {
  const { id } = useParams();
  const [contract, setContract] = useState(undefined);
  const [name, setName] = useState("");
  const [signing, setSigning] = useState(false);
  const [done, setDone] = useState(false);
  const [hasDrawn, setHasDrawn] = useState(false);
  const canvasRef = useRef(null);
  const drawing = useRef(false);

  useEffect(() => {
    document.title = "Auftragsbestätigung | JUSTIN HILFT";
    trackPageview("/vertrag");
    api
      .get(`/contracts/${id}/public`)
      .then(({ data }) => {
        setContract(data);
        setName(data.customer_name);
      })
      .catch(() => setContract(null));
  }, [id]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ratio = window.devicePixelRatio || 1;
    canvas.width = canvas.offsetWidth * ratio;
    canvas.height = canvas.offsetHeight * ratio;
    const ctx = canvas.getContext("2d");
    ctx.scale(ratio, ratio);
    ctx.lineWidth = 2.5;
    ctx.lineCap = "round";
    ctx.strokeStyle = "#0a0a0a";
  }, [contract]);

  const pos = (e) => {
    const r = canvasRef.current.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  };

  const startDraw = (e) => {
    drawing.current = true;
    const ctx = canvasRef.current.getContext("2d");
    const { x, y } = pos(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const moveDraw = (e) => {
    if (!drawing.current) return;
    const ctx = canvasRef.current.getContext("2d");
    const { x, y } = pos(e);
    ctx.lineTo(x, y);
    ctx.stroke();
    setHasDrawn(true);
  };

  const endDraw = () => {
    drawing.current = false;
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    canvas.getContext("2d").clearRect(0, 0, canvas.width, canvas.height);
    setHasDrawn(false);
  };

  const submit = async () => {
    if (!hasDrawn) {
      toast.error("Bitte unterschreibe zuerst im Feld.");
      return;
    }
    if (name.trim().length < 2) {
      toast.error("Bitte gib deinen Namen ein.");
      return;
    }
    setSigning(true);
    try {
      const signature_data = canvasRef.current.toDataURL("image/png");
      await api.post(`/contracts/${id}/sign`, { signature_name: name.trim(), signature_data });
      setDone(true);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSigning(false);
    }
  };

  if (contract === undefined) {
    return (
      <main className="min-h-screen bg-paper text-ink flex items-center justify-center" data-testid="vertrag-loading">
        <Loader2 size={24} className="animate-spin text-zinc-400" />
      </main>
    );
  }

  if (contract === null) {
    return (
      <main className="min-h-screen bg-paper text-ink flex items-center justify-center px-6" data-testid="vertrag-not-found">
        <div className="text-center">
          <p className="font-display uppercase tracking-tight text-2xl mb-4">Auftrag nicht gefunden</p>
          <Link to="/" className="text-sm text-zinc-500 hover:text-ink">Zur Startseite</Link>
        </div>
      </main>
    );
  }

  const alreadySigned = contract.status === "unterschrieben" || done;

  return (
    <main data-testid="vertrag-page" className="min-h-screen bg-paper text-ink px-6 lg:px-12 py-12">
      <div className="max-w-2xl mx-auto">
        <p className="font-display font-bold uppercase tracking-tight text-lg mb-1">
          Justin Hilft<span className="text-gold">.</span>
        </p>
        <p className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-10">Auftragsbestätigung</p>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          className="border border-black/10 bg-white p-8 lg:p-10 mb-8"
        >
          <div className="flex items-start justify-between gap-4 mb-8">
            <div>
              <p className="text-xs uppercase tracking-[0.25em] text-zinc-500 mb-1">Auftrag</p>
              <p className="font-display uppercase tracking-tight text-2xl" data-testid="vertrag-number">{contract.number}</p>
            </div>
            <FileText size={24} className="text-gold" />
          </div>

          <dl className="space-y-5 text-sm">
            <div>
              <dt className="text-xs uppercase tracking-[0.25em] text-zinc-500 mb-1">Kunde</dt>
              <dd>{contract.customer_name} · {contract.customer_email}</dd>
            </div>
            <div>
              <dt className="text-xs uppercase tracking-[0.25em] text-zinc-500 mb-1">Dienstleistung</dt>
              <dd>{SERVICE_LABELS[contract.service] || contract.service}</dd>
            </div>
            {contract.zeitraum && (
              <div>
                <dt className="text-xs uppercase tracking-[0.25em] text-zinc-500 mb-1">Zeitraum</dt>
                <dd>{contract.zeitraum}</dd>
              </div>
            )}
            {contract.date && (
              <div>
                <dt className="text-xs uppercase tracking-[0.25em] text-zinc-500 mb-1">Fester Termin</dt>
                <dd data-testid="vertrag-date">{contract.date.split("-").reverse().join(".")}</dd>
              </div>
            )}
            <div>
              <dt className="text-xs uppercase tracking-[0.25em] text-zinc-500 mb-1">Beschreibung</dt>
              <dd className="leading-relaxed text-zinc-600 whitespace-pre-wrap">{contract.description}</dd>
            </div>
            <div>
              <dt className="text-xs uppercase tracking-[0.25em] text-zinc-500 mb-1">Vergütung</dt>
              <dd className="font-display text-2xl" data-testid="vertrag-price">{contract.price}</dd>
              <dd className="text-xs text-zinc-500 mt-1">Gemäß § 19 UStG wird keine Umsatzsteuer berechnet (Kleinunternehmerregelung).</dd>
            </div>
            <div className="border-t border-black/5 pt-5">
              <dt className="text-xs uppercase tracking-[0.25em] text-zinc-500 mb-2">Zahlung per Überweisung</dt>
              <dd className="text-zinc-600 leading-relaxed">
                Kontoinhaber: Justin Mochkaai<br />
                Bank: VR Bank Ostallgäu<br />
                IBAN: DE19 7209 0000 0002 7469 64<br />
                Verwendungszweck: {contract.number}<br />
                Zahlbar innerhalb von 14 Tagen ohne Abzug.
              </dd>
            </div>
            <p className="text-xs text-zinc-500 leading-relaxed border-t border-black/5 pt-5">
              Es gelten die Allgemeinen Geschäftsbedingungen von JUSTIN HILFT
              (justinhilft.de/agb). Mit deiner Unterschrift bestätigst du den
              Auftrag zu den oben genannten Konditionen.
            </p>
          </dl>
        </motion.div>

        {alreadySigned ? (
          <div className="border border-gold/40 bg-gold/5 p-8 text-center" data-testid="vertrag-signed-state">
            <Check size={28} className="text-gold mx-auto mb-4" />
            <p className="font-display uppercase tracking-tight text-xl mb-2">Unterschrieben</p>
            <p className="text-sm text-zinc-600">
              Dieser Auftrag wurde bereits unterschrieben. Die unterschriebene
              Kopie wurde per E-Mail an dich und Justin versendet.
            </p>
          </div>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
            className="border border-black/10 bg-white p-8 lg:p-10"
          >
            <p className="text-xs uppercase tracking-[0.25em] text-zinc-500 mb-4">
              Dein Name
            </p>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              data-testid="vertrag-name-input"
              className="field-input mb-8"
              placeholder="Vor- und Nachname"
            />

            <div className="flex items-center justify-between mb-3">
              <p className="text-xs uppercase tracking-[0.25em] text-zinc-500">
                Hier unterschreiben
              </p>
              <button
                onClick={clearCanvas}
                data-testid="vertrag-clear-button"
                className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-zinc-400 hover:text-ink transition-colors duration-300"
              >
                <Eraser size={14} /> Löschen
              </button>
            </div>
            <canvas
              ref={canvasRef}
              data-testid="vertrag-signature-canvas"
              onPointerDown={startDraw}
              onPointerMove={moveDraw}
              onPointerUp={endDraw}
              onPointerLeave={endDraw}
              className="w-full h-48 border border-dashed border-black/25 bg-paper cursor-crosshair mb-8"
              style={{ touchAction: "none" }}
            />

            <button
              onClick={submit}
              disabled={signing}
              data-testid="vertrag-submit-button"
              className="group relative overflow-hidden w-full bg-ink text-paper py-4 text-xs uppercase tracking-[0.3em] disabled:opacity-50"
            >
              <span className="absolute inset-0 bg-gold translate-y-full group-hover:translate-y-0 transition-transform duration-500 ease-[cubic-bezier(0.16,1,0.3,1)]" />
              <span className="relative flex items-center justify-center gap-3 group-hover:text-ink transition-colors duration-500">
                {signing && <Loader2 size={16} className="animate-spin" />}
                {signing ? "Wird gesendet ..." : "Unterschreiben & absenden"}
              </span>
            </button>
          </motion.div>
        )}

        <p className="mt-10 text-center text-xs text-zinc-400">
          Fragen? {CONTACT.email} · {CONTACT.phoneDisplay}
        </p>
      </div>
    </main>
  );
}
